import type React from 'react';
import { useEffect, useState } from 'react';
import { clsx } from 'clsx';

import placeholder from 'assets/images/people.png';
import { isPhotoTokenFailed, markPhotoTokenFailed } from 'hooks/usePhotos';

import classes from '../Chat.module.scss';

interface ChatAvatarProps {
  src?: string;
  alt?: string;
  className?: string;
}

const extractPhotoToken = (src?: string): string | null => {
  if (!src?.trim()) return null;
  const match = src.match(/\/get_image\/([^/?]+)/);
  return match?.[1] ?? null;
};

const buildPhotoUrl = (src?: string): string | null => {
  if (!src?.trim()) return null;
  if (src.startsWith('http://') || src.startsWith('https://')) return src;
  if (src.startsWith('/assets/') || src.includes('avatar')) return src;

  const token = extractPhotoToken(src.startsWith('/') ? src : `/${src}`);
  if (token && isPhotoTokenFailed(token)) {
    return null;
  }

  const path = src.startsWith('/v1/') ? src : `/v1${src.startsWith('/') ? src : `/${src}`}`;
  return path;
};

export const ChatAvatar: React.FC<ChatAvatarProps> = ({ src, alt = '', className }) => {
  const [hasError, setHasError] = useState(false);
  const photoUrl = buildPhotoUrl(src);
  const imageSrc = !photoUrl || hasError ? placeholder : photoUrl;

  useEffect(() => {
    setHasError(false);
  }, [src]);

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={clsx(classes['chat-avatar'], className)}
      onError={() => {
        const token = extractPhotoToken(src);
        if (token) {
          markPhotoTokenFailed(token);
        }
        setHasError(true);
      }}
    />
  );
};
